/**
 * Auth constants shared by the server, the game worker and the browser client.
 *
 * The cookie name and lifetime live here rather than in `apps/server` because the game worker
 * (card 9) has to recognise the same session cookie the API server sets, and because a client
 * that plans a reconnect needs to know how long a ticket stays good.
 */

/**
 * Name of the httpOnly session cookie. Prefixed so it cannot collide with a cookie set by
 * another service sharing `localhost` during development.
 */
export const SESSION_COOKIE_NAME = 'chompdome_session';

/**
 * 30 days. Longer than a typical web session on purpose: a player who comes back to an .io game
 * a fortnight later should still own their stats rather than land on a fresh guest row.
 */
export const SESSION_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

/**
 * WebSocket ticket lifetime. Long enough to cover a slow mobile handshake, short enough that a
 * ticket copied out of a network log is useless by the time anyone reads it. Tickets are also
 * single-use, so this is the *outer* bound, not the expected lifetime.
 */
export const WS_TICKET_TTL_SECONDS = 30;

/**
 * THE NATIVE SESSION ENVELOPE. The credential does not change; only how it travels does.
 *
 * A Capacitor shell cannot hold the session cookie above, and not merely as an inconvenience:
 *
 * - a bundled build is cross-site (`NATIVE_APP_ORIGINS` -> the API origin), so the cookie is a
 *   third-party cookie in a WebView that is entitled to refuse it; and
 * - Capacitor's iOS cookie layer MANGLES `Set-Cookie`, splitting the header on commas and
 *   shredding any cookie carrying a date (ionic-team/capacitor#6280, unfixed since 2023).
 *
 * Weakening `SameSite` would trade real CSRF posture for something that still breaks on iOS. So
 * the shell gets the *same opaque `user_sessions.id`* the cookie would have carried — not a JWT,
 * not a second token type — in a `X-Session-Id` response header, keeps it in the Keychain /
 * Keystore, and sends it back as `Authorization: Bearer <id>`. The server routes that into the
 * same `resolveSession` path the cookie uses, so revocation, the session cache and the expiry
 * checks are untouched and custody of the credential stays server-side.
 *
 * The header is emitted ONLY to a caller that announced itself with `X-Client-Platform: native`,
 * so a browser's response is byte for byte what it was before any of this existed.
 */

/** Where a freshly minted session id is returned to a native client. */
export const SESSION_ID_HEADER = 'X-Session-Id';

/** How a native client announces itself, and therefore asks for `SESSION_ID_HEADER`. */
export const CLIENT_PLATFORM_HEADER = 'X-Client-Platform';

/** The one value of `CLIENT_PLATFORM_HEADER` that means anything to the server. */
export const NATIVE_CLIENT_PLATFORM = 'native';
